import { debounce } from "@mui/material";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetSystemDropdownsQuery,
  useLazyGetPartnersSearchListQuery,
} from "src/shared/api/system";
import {
  EFilterCalendarType,
  EFilterType,
  IFilterModel,
} from "src/shared/components/Table/model/types";
import { EAgencies, INameValue } from "src/shared/types/common.types";
import { IBaseQueryParams } from "src/shared/types/query";
import { queryCleanEmpty } from "src/shared/utils/url/query.helpers";
import { usePreviousMonth } from "../usePreviousMonth";
import { useTableSearchParams } from "../useTableSearchParams";

export interface IGetFinalReportsCommonQuery extends IBaseQueryParams {
  agency?: string;
  partner_id?: string;
  report_month: string;
}

interface IUseFinalReportsFilters {
  partnerName?: string;
  agencyFilter?: boolean;
}

export function useFinalReportsFilters({
  partnerName,
  agencyFilter,
}: IUseFinalReportsFilters) {
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnersList, setPartnersList] = useState<
    Array<INameValue<number> & { agency: INameValue<EAgencies> }>
  >([]);

  const {
    paramsObject,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
    activeTab,
    setActiveTab,
  } = useTableSearchParams<IGetFinalReportsCommonQuery>({
    tabName: "agency",
  });
  const { sort, per_page, page, partner_id, agency, search, report_month } =
    paramsObject;
  const reportMonth = usePreviousMonth();
  const query = queryCleanEmpty({
    search,
    sort,
    per_page,
    page: String(page ? Number(page) : 1),
    partner_id,
    agency,
    report_month: reportMonth,
  });

  const { data: dropdowns } = useGetSystemDropdownsQuery();
  const [searchPartners, { isFetching: isPartnersFetching }] =
    useLazyGetPartnersSearchListQuery();

  const searchPartner = useCallback(
    (value: string) => {
      if (value.length > 2) {
        setPartnerSearch(value);
        searchPartners({ name: value, agencies: agency ? [agency] : undefined })
          .unwrap()
          .then((resp) => {
            setPartnersList(resp);
          })
          .catch(() => {
            enqueueSnackbar("Произошла ошибка", {
              variant: "error",
              handleClose: closeSnackbar,
            });
          });
      }
    },
    [searchPartners, agency],
  );

  const debouncedPartnerSearch = useMemo(
    () => debounce(searchPartner, 1000),
    [searchPartner],
  );

  useEffect(() => {
    return () => {
      debouncedPartnerSearch.clear?.();
    };
  }, [debouncedPartnerSearch]);

  const filters: IFilterModel["filters"] = useMemo(
    () => [
      {
        name: "partner_id",
        // for example if need multiple partners
        // type: EFilterType.MULTI_AUTOCOMPLETE,
        // value: Array.isArray(partner_id)
        //   ? partner_id
        //   : partner_id
        //     ? [partner_id]
        //     : [],
        // placeholder: "Минимум 3 символа",
        loading: isPartnersFetching,
        type: EFilterType.SINGLE_AUTOCOMPLETE,
        value: partner_id || null,
        title: "Партнер",
        options: partnersList || [],
        onInputChange: (_e, value, reason) => {
          if (reason !== "input") return;
          debouncedPartnerSearch(value);
        },
      },
      ...(agencyFilter
        ? [
            {
              name: "agency",
              type: EFilterType.SINGLE_SELECT,
              value: agency || "",
              title: "Компания",
              options: dropdowns?.agencies || [],
            },
          ]
        : []),
      {
        name: "report_month",
        type: EFilterCalendarType.MONTH,
        value: report_month || null,
        title: "За период",
      },
    ],
    [
      agency,
      agencyFilter,
      debouncedPartnerSearch,
      dropdowns?.agencies,
      isPartnersFetching,
      partner_id,
      partnersList,
      report_month,
    ],
  );

  useEffect(() => {
    if (partner_id && !partnerSearch && !partnersList.length && partnerName) {
      searchPartner(partnerName);
    }
  }, [
    partnerName,
    partnerSearch,
    partner_id,
    partnersList.length,
    searchPartner,
  ]);

  // here we filter partners by selected agency if agency changed

  const filteredPartnersList = useMemo(() => {
    if (!agency) return partnersList;

    return partnersList.filter((v) => v.agency.value === Number(agency));
  }, [agency, partnersList]);

  useEffect(() => {
    if (!agency && !partner_id) return;

    const isValidPartner = filteredPartnersList.some(
      (v) => v.value === Number(partner_id),
    );

    if (!isValidPartner) {
      onResetFilter(["partner_id"]);
    }
  }, [agency, partner_id, filteredPartnersList, onResetFilter]);

  return {
    query,
    filters,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
    activeTab,
    setActiveTab,
  };
}
